import { useEffect, useMemo, useState } from "react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Trash2, Lock, Unlock } from "lucide-react";

const WEEKDAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoDate(d) {
  // Local-day ISO (YYYY-MM-DD), avoids UTC tz shifts
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function StaffPricingPage() {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [days, setDays] = useState([]);
  const [base, setBase] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());  // ISO date strings
  const [newPrice, setNewPrice] = useState("");
  const [saving, setSaving] = useState(false);

  // Load rooms
  useEffect(() => {
    api.get("/rooms").then((r) => {
      setRooms(r.data);
      if (r.data[0]) setRoomId(r.data[0].id);
    }).catch(() => toast.error("Could not load rooms"));
  }, []);

  // Compute month grid (Mon-start, 6 rows × 7 cols)
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // 0=Mon ... 6=Sun (JS Date getDay: 0=Sun..6=Sat → convert)
    const firstWeekday = (first.getDay() + 6) % 7;
    const totalCells = 42;
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const d = addDays(first, i - firstWeekday);
      const inMonth = d.getMonth() === cursor.getMonth();
      cells.push({ date: d, iso: isoDate(d), inMonth, last });
    }
    return cells;
  }, [cursor]);

  // Load pricing calendar
  async function load() {
    if (!roomId) return;
    setLoading(true);
    setSelected(new Set());
    try {
      const start = isoDate(grid[0].date);
      const end = isoDate(grid[grid.length - 1].date);
      const r = await api.get(`/admin/rooms/${roomId}/pricing-calendar`, { params: { start, end } });
      setBase(r.data.base_price);
      setDays(r.data.days || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pricing");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roomId, cursor]);

  const dayMap = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.date] = d; });
    return m;
  }, [days]);

  function toggleSelect(iso) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  }

  function selectAllInMonth() {
    const all = new Set(grid.filter((c) => c.inMonth).map((c) => c.iso));
    setSelected(all);
  }
  function selectWeekends() {
    const all = new Set(grid.filter((c) => c.inMonth).filter((c) => {
      const wd = (c.date.getDay() + 6) % 7;
      return wd === 4 || wd === 5;  // Fri, Sat
    }).map((c) => c.iso));
    setSelected(all);
  }
  function clearSel() { setSelected(new Set()); }

  async function applyPrice() {
    if (!selected.size) { toast.error("Pick at least one date"); return; }
    const p = Number(newPrice);
    if (!p || p <= 0) { toast.error("Enter a valid price"); return; }
    setSaving(true);
    try {
      const isos = [...selected].sort();
      const start = isos[0];
      const end = isos[isos.length - 1];
      const weekdaysInSel = new Set(isos.map((s) => (new Date(s + "T00:00:00").getDay() + 6) % 7));
      const allDaysInRange = (() => {
        const out = [];
        let d = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        while (d <= e) { out.push(isoDate(d)); d = addDays(d, 1); }
        return out;
      })();
      const isContiguous = allDaysInRange.every((iso) => selected.has(iso));
      const payload = { start, end, price: p };
      if (!isContiguous) payload.weekdays = [...weekdaysInSel];
      await api.put(`/admin/rooms/${roomId}/pricing`, payload);
      toast.success(`Updated ${selected.size} night${selected.size !== 1 ? "s" : ""} to €${p}`);
      setNewPrice("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  async function setAvailability(available) {
    if (!selected.size) { toast.error("Pick dates first"); return; }
    setSaving(true);
    try {
      const isos = [...selected].sort();
      const start = isos[0];
      const end = isos[isos.length - 1];
      const weekdaysInSel = new Set(isos.map((s) => (new Date(s + "T00:00:00").getDay() + 6) % 7));
      const allDaysInRange = (() => {
        const out = [];
        let d = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        while (d <= e) { out.push(isoDate(d)); d = addDays(d, 1); }
        return out;
      })();
      const isContiguous = allDaysInRange.every((iso) => selected.has(iso));
      const payload = { start, end, available };
      if (!isContiguous) payload.weekdays = [...weekdaysInSel];
      await api.put(`/admin/rooms/${roomId}/availability`, payload);
      toast.success(available ? "Marked as available" : "Marked as unavailable");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  async function clearOverrides() {
    if (!selected.size) { toast.error("Pick dates first"); return; }
    if (!window.confirm(`Revert ${selected.size} night(s) to base price €${base}?`)) return;
    setSaving(true);
    try {
      const isos = [...selected].sort();
      const start = isos[0];
      const end = isos[isos.length - 1];
      const weekdaysInSel = new Set(isos.map((s) => (new Date(s + "T00:00:00").getDay() + 6) % 7));
      const allDaysInRange = (() => {
        const out = [];
        let d = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        while (d <= e) { out.push(isoDate(d)); d = addDays(d, 1); }
        return out;
      })();
      const isContiguous = allDaysInRange.every((iso) => selected.has(iso));
      const payload = { start, end };
      if (!isContiguous) payload.weekdays = [...weekdaysInSel];
      await api.delete(`/admin/rooms/${roomId}/pricing`, { data: payload });
      toast.success("Reverted to base price");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <StaffLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Pricing</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Calendar &amp; bulk edit</h1>
            <p className="text-sm text-[#71717A] mt-1">Set seasonal rates, weekend bumps, or last-minute deals. External partner bookings (Booking.com, Airbnb) appear here too.</p>
          </div>

          <select
            data-testid="pricing-room-select"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            className="border border-[#E4E4E7] rounded-md px-3 py-2 text-sm bg-white min-w-[200px]"
          >
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          {/* Calendar toolbar */}
          <div className="px-4 sm:px-6 py-3 border-b border-[#E4E4E7] flex flex-wrap items-center gap-3">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              data-testid="pricing-prev-month" className="p-2 hover:bg-[#FAFAFA] rounded-md"><ChevronLeft size={16} /></button>
            <div className="font-display text-xl min-w-[180px] text-center">
              {cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })}
            </div>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              data-testid="pricing-next-month" className="p-2 hover:bg-[#FAFAFA] rounded-md"><ChevronRight size={16} /></button>
            {loading && <Loader2 size={14} className="animate-spin text-[#71717A] ml-auto" />}
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#71717A] bg-[#FAFAFA] border-b border-[#E4E4E7]">
            {WEEKDAYS_SHORT.map((w) => <div key={w} className="px-2 py-2 text-center">{w}</div>)}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {grid.map((c) => {
              const info = dayMap[c.iso];
              const isSel = selected.has(c.iso);
              const wd = (c.date.getDay() + 6) % 7;
              const isWeekend = wd === 4 || wd === 5;
              const todayIso = isoDate(new Date());
              const isPast = c.iso < todayIso;
              const booked = info?.booked;
              const isExternal = booked && info?.source === "external";
              const isOverride = info?.has_override;
              const price = info?.price ?? base;
              const disabled = !c.inMonth || isPast;
              const cls = [
                "relative aspect-square sm:aspect-auto sm:min-h-[88px] border-b border-r border-[#E4E4E7] p-2 text-left text-xs flex flex-col gap-1 transition-colors",
                disabled ? "bg-[#FAFAFA] text-[#A1A1AA] cursor-default" : "cursor-pointer hover:bg-[#FAFAFA]",
                isSel ? "ring-2 ring-brand-primary ring-inset bg-brand-secondary/40" : "",
                info?.blocked ? "bg-rose-50" : booked ? "bg-zinc-100" : "",
                isWeekend && !booked && !info?.blocked && !disabled ? "bg-amber-50/30" : "",
              ].join(" ");
              return (
                <button
                  key={c.iso}
                  data-testid={`day-${c.iso}`}
                  disabled={disabled}
                  onClick={() => !disabled && toggleSelect(c.iso)}
                  className={cls}
                >
                  <div className="flex items-center justify-between">
                    <span className={c.inMonth ? "font-medium" : ""}>{c.date.getDate()}</span>
                    {info?.blocked && <span className="text-[9px] px-1 py-0.5 bg-rose-200 text-rose-800 rounded">BLK</span>}
                    {isExternal && <span className="text-[9px] px-1 py-0.5 bg-violet-100 text-violet-700 rounded">OTA</span>}
                    {booked && !isExternal && <span className="text-[9px] px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded">BKD</span>}
                  </div>
                  {!disabled && (
                    <div className={`mt-auto font-mono text-[11px] ${info?.blocked ? "text-rose-400 line-through" : isOverride ? "text-brand-primary font-semibold" : "text-[#52525B]"}`}>
                      €{price}
                    </div>
                  )}
                  {info?.label && booked && (
                    <div className="text-[9px] text-[#71717A] truncate">{info.label}</div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Toolbar */}
          <div className="px-4 sm:px-6 py-4 border-t border-[#E4E4E7] flex flex-col gap-3 flex-wrap">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-sm font-medium">{selected.size} night{selected.size !== 1 ? "s" : ""} selected</div>
              <button data-testid="pricing-select-all" onClick={selectAllInMonth} className="text-xs px-3 py-1.5 border border-[#E4E4E7] rounded-md hover:bg-[#FAFAFA]">Whole month</button>
              <button data-testid="pricing-select-weekends" onClick={selectWeekends} className="text-xs px-3 py-1.5 border border-[#E4E4E7] rounded-md hover:bg-[#FAFAFA]">Weekends only</button>
              <button data-testid="pricing-clear-sel" onClick={clearSel} className="text-xs px-3 py-1.5 border border-[#E4E4E7] rounded-md hover:bg-[#FAFAFA]">Clear</button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#71717A]">€</span>
                <input
                  data-testid="pricing-new-price-input"
                  type="number"
                  step="1"
                  min="1"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="New price"
                  className="border border-[#E4E4E7] rounded-md px-3 py-1.5 text-sm w-28"
                />
                <button
                  data-testid="pricing-apply"
                  onClick={applyPrice}
                  disabled={saving || !selected.size}
                  className="bg-brand-primary text-white text-sm px-4 py-1.5 rounded-md hover:bg-brand-primaryHover disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Apply price"}
                </button>
                <button
                  data-testid="pricing-revert"
                  onClick={clearOverrides}
                  disabled={saving || !selected.size}
                  className="text-sm px-3 py-1.5 border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1"
                ><Trash2 size={13} /> Revert</button>
              </div>

              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  data-testid="pricing-mark-unavailable"
                  onClick={() => setAvailability(false)}
                  disabled={saving || !selected.size}
                  className="text-sm px-3 py-1.5 border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1"
                ><Lock size={13} /> Mark unavailable</button>
                <button
                  data-testid="pricing-mark-available"
                  onClick={() => setAvailability(true)}
                  disabled={saving || !selected.size}
                  className="text-sm px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"
                ><Unlock size={13} /> Mark available</button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs text-[#71717A] flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-amber-50/60 border border-amber-200 rounded-sm" /> Weekend</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-zinc-100 border border-zinc-200 rounded-sm" /> Booked (your site)</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-violet-100 border border-violet-200 rounded-sm" /> OTA (Booking · Airbnb)</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-rose-50 border border-rose-200 rounded-sm" /> Blocked (manual)</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 ring-2 ring-brand-primary ring-inset bg-brand-secondary/40 rounded-sm" /> Selected</span>
          <span className="inline-flex items-center gap-2 text-brand-primary font-semibold">€</span><span>Override</span>
        </div>
      </div>
    </StaffLayout>
  );
}
